import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { User, ArrowLeft, MessageCircle, MapPin } from "lucide-react";
import api, { resolveAsset } from "@/lib/api";
import PageSeo from "@/components/site/PageSeo";
import { useSettings } from "@/contexts/SettingsContext";
import { newsDetailSeo } from "@/lib/seo";
import { resolveWhatsAppDigits, whatsappUrl } from "@/lib/contact";

const NewsDetailPage = () => {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const { settings, pages = {} } = useSettings() || {};
    const waDigits = resolveWhatsAppDigits(settings);

    useEffect(() => {
        setArticle(null);
        setNotFound(false);
        const ctrl = new AbortController();
        api.get(`/news/${slug}`, { signal: ctrl.signal })
            .then((r) => setArticle(r.data))
            .catch((err) => {
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
                setNotFound(true);
            });
        return () => ctrl.abort();
    }, [slug]);

    if (notFound) {
        return (
            <div className="container-x py-20 text-center" data-testid="news-not-found">
                <h1 className="font-display font-bold text-3xl text-[#0A2540]">Article not found</h1>
                <p className="text-sm text-[#4a4a52] mt-2">This story may have been removed.</p>
                <Link to="/news" className="btn btn-primary mt-6">Back to news</Link>
            </div>
        );
    }

    if (!article) return <div className="container-x py-20 text-center text-[#7a7a84]">Loading…</div>;

    const seo = newsDetailSeo(article, pages, settings);

    return (
        <article className="container-x py-10 max-w-4xl" data-testid="news-detail-page">
            <PageSeo
                pageKey="news_detail"
                title={seo.title}
                description={seo.description}
                path={`/news/${slug}`}
                image={seo.image}
            />
            <Link to="/news" className="text-sm text-[#ED1C24] inline-flex items-center gap-1 mb-5"><ArrowLeft size={14} /> Back to News</Link>
            <span className="chip chip-red text-[10px]">{article.category}</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-4 leading-tight">{article.title}</h1>
            <p className="text-sm text-[#7a7a84] mt-3 flex items-center gap-2"><User size={14} /> {article.author}</p>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden bike-card mt-6">
                <img src={resolveAsset(article.image)} alt={article.title} className="w-full h-full object-cover" />
            </div>

            <p className="text-lg text-[#4a4a52] leading-relaxed mt-8 font-medium">{article.excerpt}</p>
            <div className="text-base text-[#4a4a52] leading-relaxed mt-4 whitespace-pre-line">{article.content}</div>

            <div className="mt-12 bike-card p-6 sm:p-8 bg-[#F4F6F8] border border-zinc-200" data-testid="news-detail-cta">
                <h2 className="font-display font-bold text-2xl text-[#0A2540]">{pages.news_detail_cta_title || "Ready to buy?"}</h2>
                <p className="text-sm text-zinc-500 mt-2 max-w-lg">{pages.news_detail_cta_sub}</p>
                <div className="flex flex-wrap gap-3 mt-5">
                    <Link to="/bikes" className="btn btn-primary btn-sm min-h-[44px]">Browse bikes</Link>
                    <Link to="/dealers" className="btn btn-white btn-sm min-h-[44px]"><MapPin size={14} /> Find outlet</Link>
                    <a href={whatsappUrl(waDigits, "Hi Servall, I read your article and want help choosing a bike.")} target="_blank" rel="noopener noreferrer" className="btn btn-white btn-sm min-h-[44px]" data-testid="news-detail-whatsapp">
                        <MessageCircle size={14} /> WhatsApp us
                    </a>
                </div>
            </div>
        </article>
    );
};

export default NewsDetailPage;
